"use client";

import { useRouter, usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { Check, X } from "lucide-react";
import { useFavorites } from "@/lib/use-favorites";

interface FavoriteButtonProps {
	slug: string;
	className?: string;
}

/**
 * Save-Button — Nike-check Aesthetic.
 * Auth-gated: click without account → portal-rendered modal (escapes card stacking).
 */
export function FavoriteButton({ slug, className = "" }: FavoriteButtonProps) {
	const t = useTranslations("favorites");
	const { has, toggle, mounted } = useFavorites();
	const router = useRouter();
	const pathname = usePathname();
	const [isAuthed, setIsAuthed] = useState(false);
	const [savedTick, setSavedTick] = useState(0);
	const [toastVisible, setToastVisible] = useState(false);
	const [showAuthPrompt, setShowAuthPrompt] = useState(false);

	useEffect(() => {
		if (typeof document === "undefined") return;
		queueMicrotask(() => {
			const hasAuth = document.cookie.split(";").some((c) => c.trim().includes("saleor_auth_access_token"));
			setIsAuthed(hasAuth);
		});
	}, []);

	// Modal: ESC to close, body-scroll lock
	useEffect(() => {
		if (!showAuthPrompt) return;
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setShowAuthPrompt(false);
		};
		document.addEventListener("keydown", handleKey);
		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", handleKey);
			document.body.style.overflow = originalOverflow;
		};
	}, [showAuthPrompt]);

	if (!mounted) return null;

	const isFavorite = has(slug);

	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isAuthed) {
			setShowAuthPrompt(true);
			return;
		}
		const wasNotFavorite = !isFavorite;
		toggle(slug);
		if (wasNotFavorite) {
			setSavedTick((n) => n + 1);
			setToastVisible(true);
			window.setTimeout(() => setToastVisible(false), 1800);
		}
	};

	const closeModal = (e?: React.MouseEvent | React.TouchEvent) => {
		// Prevent the click from synthesizing onto whatever is underneath the modal
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		setShowAuthPrompt(false);
	};

	const goToLogin = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		const next = encodeURIComponent(pathname || "/");
		router.push(`/login?next=${next}`);
	};

	const modal =
		showAuthPrompt && typeof document !== "undefined"
			? createPortal(
					<div
						role="dialog"
						aria-modal="true"
						onClick={closeModal}
						className="fixed inset-0 z-[200] flex animate-[modalFadeIn_0.2s_ease-out] items-center justify-center bg-black/60 p-4 backdrop-blur-md"
					>
						<div
							onClick={(e) => e.stopPropagation()}
							className="relative w-full max-w-sm animate-[modalPopIn_0.25s_ease-out] rounded-2xl border border-border bg-white p-6 text-left shadow-[0_30px_60px_-15px_rgba(8,39,74,0.4)]"
						>
							<button
								type="button"
								onClick={closeModal}
								aria-label="Schließen"
								className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
							>
								<X className="h-4 w-4" />
							</button>

							<div className="flex flex-col items-center text-center">
								<div className="bg-primary-hero/10 flex h-12 w-12 items-center justify-center rounded-full">
									<Check className="h-5 w-5 text-primary-hero" strokeWidth={2.5} />
								</div>
								<div className="mt-4 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-primary-hero">
									Account benötigt
								</div>
								<h3 className="mt-2 font-display text-[1.15rem] font-medium tracking-[-0.01em] text-foreground">
									Speichere deine Favoriten
								</h3>
								<p className="mt-2 text-[0.85rem] leading-relaxed text-muted-foreground">
									Erstell ein kostenloses Konto, um Peptide in deinen Favoriten zu speichern —
									geräteübergreifend & jederzeit abrufbar.
								</p>
								<button
									type="button"
									onClick={goToLogin}
									className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-primary-hero font-mono text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-primary-hero-foreground shadow-md transition-colors hover:bg-primary"
								>
									Anmelden / Registrieren →
								</button>
								<button
									type="button"
									onClick={closeModal}
									className="mt-2 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
								>
									Vielleicht später
								</button>
							</div>
						</div>
					</div>,
					document.body,
				)
			: null;

	return (
		<div className="relative">
			<button
				type="button"
				onClick={handleClick}
				aria-label={isFavorite ? t("remove") : t("add")}
				className={`group/save inline-flex h-7 w-7 items-center justify-center rounded-full border transition-all duration-200 hover:scale-105 active:scale-95 ${
					isFavorite
						? "border-primary-hero bg-primary-hero text-primary-hero-foreground shadow-sm"
						: "border-primary-hero/40 bg-white/90 text-primary-hero hover:border-primary-hero hover:bg-white"
				} ${className}`}
			>
				<Check
					key={savedTick}
					className={`h-3 w-3 ${savedTick > 0 ? "animate-[spinOnce_0.5s_ease-out]" : ""} ${
						isFavorite ? "scale-110 stroke-[3]" : "group-hover/save:opacity-100 stroke-[2.5] opacity-70"
					}`}
				/>
			</button>

			{/* Toast — "✓ Gespeichert" 1.8s */}
			{toastVisible && (
				<div
					role="status"
					className="border-success/30 pointer-events-none absolute right-0 top-full z-30 mt-1.5 animate-[savedPop_1.8s_ease-out_forwards] whitespace-nowrap rounded-md border bg-success px-2 py-1 font-mono text-[0.55rem] font-semibold uppercase tracking-[0.14em] text-success-foreground shadow-md"
				>
					✓ Gespeichert
				</div>
			)}

			{modal}
		</div>
	);
}
